import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import { fileApi } from '@/entities/file'
import type { AxiosRequestConfig } from 'axios'
import type {
  IOverrideDeferralPayload,
  IOverrideDeferralResponse,
} from '../model/override-deferral.types'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const overrideDeferralApi = {
  /** Stores the scan through the shared files service and returns its download URL. */
  uploadCertificateScan: async (file: File) => {
    const { data } = await fileApi.uploadFile(file, {
      _skipGlobalErrorHandler: true,
    } as AxiosRequestConfig)
    return data.id
  },

  override: async (donorId: string, payload: IOverrideDeferralPayload) => {
    const response = await apiVisitEngine.post<IOverrideDeferralResponse>(
      `/v1/admin/donors/${donorId}/override-deferral`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },
}

// ── Mutation hook ─────────────────────────────────────────────────────────────

interface IOverrideDeferralVariables {
  donorId: string
  deferralId: string
  reasonText: string
  certificateFile: File
}

export const useOverrideDeferralMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: IOverrideDeferralVariables) => {
      // The scan has to exist in the files service before the override is sent —
      // the endpoint only accepts a URL, not the bytes.
      const certificateScanId = await overrideDeferralApi.uploadCertificateScan(
        variables.certificateFile,
      )

      return overrideDeferralApi.override(variables.donorId, {
        deferral_id: variables.deferralId,
        reason_text: variables.reasonText,
        certificate_scan_file_id: certificateScanId,
      })
    },
    onSuccess: () => {
      // The row stays in the journal as LIFTED, and the card's status changes.
      queryClient.invalidateQueries({ queryKey: queryKeys.deferrals.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.visits.admitted() })
      queryClient.invalidateQueries({ queryKey: queryKeys.donorCard.all })
    },
  })
}
